/**
 * StateSideEffectsSection - state-entry side effects for the Workflow Designer.
 *
 * These effects are stored on `WorkflowState.side_effects`; the engine only
 * dispatches them after state-entry availability rules pass and the instance
 * is committed into the state.
 */

import { useMemo, useState } from 'react'
import { Pencil, Plus, X } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import {
  useEffectHandlerCatalog,
  useUpdateState,
  type EffectHandlerCatalogEntry,
  type WorkflowSideEffectEntry,
  type WorkflowStateAdmin,
} from '@/admin/hooks/use-workflow-definitions'
import { toastApiError } from '@/shared/lib/api-error'

import { HandlerConfigForm } from './action-modal/handler-config-form'

const PHASE_OPTIONS = [
  { value: 'pre_commit', label: 'pre_commit - before save' },
  { value: 'on_commit', label: 'on_commit - after save' },
  { value: 'async', label: 'async - background worker' },
]

const FAILURE_POLICY_OPTIONS = [
  { value: 'fail_closed', label: 'fail_closed - rollback on error' },
  { value: 'log_and_continue', label: 'log_and_continue - log and continue' },
  { value: 'retry_with_backoff', label: 'retry_with_backoff - retry later' },
  { value: 'dead_letter', label: 'dead_letter - mark failed' },
]

const FALLBACK_SCHEMAS: Record<string, Record<string, unknown>> = {
  call_service: {
    type: 'object',
    required: ['service'],
    properties: {
      service: { type: 'string' },
      args: { type: 'array' },
      kwargs: { type: 'object' },
    },
  },
  send_email: {
    type: 'object',
    required: ['to', 'template'],
    properties: {
      to: { type: 'string' },
      from_email: { type: 'string' },
      subject: { type: 'string' },
      template: { type: 'string' },
      context: { type: 'object' },
    },
  },
}

interface StateSideEffectsSectionProps {
  workflowUuid: string
  state: WorkflowStateAdmin | undefined
}

export function StateSideEffectsSection({
  workflowUuid,
  state,
}: StateSideEffectsSectionProps) {
  const { data: catalog = [], isLoading } = useEffectHandlerCatalog()
  const update = useUpdateState(workflowUuid)
  const [effects, setEffects] = useState<WorkflowSideEffectEntry[]>(
    normaliseEffects(state?.side_effects),
  )
  const [deleting, setDeleting] = useState<{
    effect: WorkflowSideEffectEntry
    index: number
  } | null>(null)
  const [editing, setEditing] = useState<{
    effect: WorkflowSideEffectEntry
    index: number
  } | null>(null)

  const byKey = useMemo(
    () => new Map(catalog.map((handler) => [handler.key, handler])),
    [catalog],
  )

  if (!state) {
    return (
      <div className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-5 text-center text-[11px] text-[var(--ink-3)]">
        Save the state first - side effects attach once the state has a UUID.
      </div>
    )
  }

  async function persist(next: WorkflowSideEffectEntry[], successMessage: string) {
    if (!state) return
    try {
      const updated = await update.mutateAsync({
        uuid: state.uuid,
        side_effects: next,
      })
      setEffects(normaliseEffects(updated.side_effects))
      toast.success(successMessage)
    } catch (err) {
      toastApiError(err, 'Failed to update side effects')
    }
  }

  async function addHandler(key: string) {
    const handler = byKey.get(key)
    if (!handler) return
    await persist([...effects, buildEffect(handler)], 'Side effect added')
  }

  async function handleDelete() {
    if (!deleting) return
    const next = effects.filter((_, index) => index !== deleting.index)
    await persist(next, 'Side effect deleted')
    setDeleting(null)
  }

  async function handleEditSave(nextEffect: WorkflowSideEffectEntry) {
    if (!editing) return
    const next = effects.map((effect, index) =>
      index === editing.index ? nextEffect : effect,
    )
    await persist(next, 'Side effect updated')
    setEditing(null)
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <h3 className="text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
            Side effects
          </h3>
          <p className="text-[10px] text-[var(--ink-3)]">
            Run only after every availability rule passes and this state is
            activated.
          </p>
        </div>
        <AddSideEffectSelect
          handlers={catalog}
          loading={isLoading || update.isPending}
          onAdd={addHandler}
        />
      </div>

      {effects.length === 0 ? (
        <div className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
          No side effects yet. Add one to run downstream work when this state is
          entered.
        </div>
      ) : (
        <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
          {effects.map((effect, index) => (
            <SideEffectRow
              key={`${effect.handler}-${index}`}
              effect={effect}
              handler={byKey.get(effect.handler)}
              onEdit={() => setEditing({ effect, index })}
              onDelete={() => setDeleting({ effect, index })}
            />
          ))}
        </div>
      )}

      <ConfirmationDialog
        open={!!deleting}
        onOpenChange={(next) => {
          if (!next) setDeleting(null)
        }}
        title={`Delete ${effectTitle(deleting?.effect, byKey)}?`}
        description="This removes the side effect from the state. Future instances entering this state will no longer run it."
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        isPending={update.isPending}
        onConfirm={handleDelete}
      />
      <SideEffectEditDialog
        key={editing ? `${editing.effect.handler}-${editing.index}` : 'closed'}
        open={!!editing}
        effect={editing?.effect}
        handler={editing ? byKey.get(editing.effect.handler) : undefined}
        onOpenChange={(next) => {
          if (!next) setEditing(null)
        }}
        onSave={handleEditSave}
        isPending={update.isPending}
      />
    </section>
  )
}

function AddSideEffectSelect({
  handlers,
  loading,
  onAdd,
}: {
  handlers: EffectHandlerCatalogEntry[]
  loading: boolean
  onAdd: (key: string) => void
}) {
  return (
    <Select
      value=""
      disabled={loading}
      onValueChange={(next: string) => {
        if (next) onAdd(next)
      }}
    >
      <SelectTrigger className="h-7 w-[190px] text-[11px]">
        <span className="flex items-center gap-1">
          <Plus size={11} />
          <SelectValue placeholder="Add side effect..." />
        </span>
      </SelectTrigger>
      <SelectContent>
        {loading && (
          <div className="px-3 py-2 text-[11px] text-[var(--ink-3)]">
            Loading...
          </div>
        )}
        {!loading && handlers.length === 0 && (
          <div className="px-3 py-2 text-[11px] text-[var(--ink-3)]">
            No handlers registered.
          </div>
        )}
        {handlers.map((handler) => (
          <SelectItem key={handler.key} value={handler.key}>
            <div className="flex flex-col">
              <span className="text-[11px]">
                {handler.label || humanize(handler.key)}
              </span>
              <span className="font-mono text-[10px] text-[var(--ink-3)]">
                {handler.key}
              </span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function SideEffectRow({
  effect,
  handler,
  onEdit,
  onDelete,
}: {
  effect: WorkflowSideEffectEntry
  handler: EffectHandlerCatalogEntry | undefined
  onEdit: () => void
  onDelete: () => void
}) {
  const title = handler?.label || humanize(effect.handler)
  const summary = effectSummary(effect, handler)
  return (
    <div className="flex items-center gap-2 border-t-[0.5px] border-t-[var(--rule)] bg-[var(--bg)] px-3 py-2 first:border-t-0">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-[12px] text-[var(--ink)]">
            {title}
          </span>
          <span className="font-mono text-[10px] text-[var(--ink-3)]">
            {effect.handler}
          </span>
          <span className="text-[10px] text-[var(--ink-3)]">
            {effect.phase ?? 'on_commit'}
          </span>
        </div>
        <span className="truncate text-[10px] text-[var(--ink-3)]">
          {summary}
        </span>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        onClick={onEdit}
        title="Edit side effect"
      >
        <Pencil size={13} />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        onClick={onDelete}
        title="Delete side effect"
      >
        <X size={13} />
      </Button>
    </div>
  )
}

function SideEffectEditDialog({
  open,
  effect,
  handler,
  onOpenChange,
  onSave,
  isPending,
}: {
  open: boolean
  effect: WorkflowSideEffectEntry | undefined
  handler: EffectHandlerCatalogEntry | undefined
  onOpenChange: (next: boolean) => void
  onSave: (next: WorkflowSideEffectEntry) => Promise<void>
  isPending: boolean
}) {
  const [draft, setDraft] = useState<WorkflowSideEffectEntry>(
    effect ? normaliseEffects([effect])[0] : buildEmptyEffect(),
  )
  const schema = handler?.config_schema ?? FALLBACK_SCHEMAS[draft.handler]

  if (!effect) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle>Edit {handler?.label || humanize(draft.handler)}</DialogTitle>
        </DialogHeader>
        <div className="flex max-h-[65vh] flex-col gap-4 overflow-y-auto pr-1">
          <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2">
            <p className="text-[11px] text-[var(--ink-2)]">
              {effectSummary(draft, handler)}
            </p>
            {!handler && (
              <p className="mt-1 text-[10px] text-[var(--ink-3)]">
                Handler metadata was not returned by the registry, so this form
                uses the stored configuration and a fallback schema when known.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Phase
              </Label>
              <Select
                value={draft.phase ?? 'on_commit'}
                onValueChange={(phase: string) =>
                  setDraft((current) => ({ ...current, phase }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PHASE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Failure policy
              </Label>
              <Select
                value={draft.failure_policy ?? 'log_and_continue'}
                onValueChange={(failure_policy: string) =>
                  setDraft((current) => ({ ...current, failure_policy }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FAILURE_POLICY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Handler config
            </Label>
            <HandlerConfigForm
              schema={schema}
              value={(draft.config ?? {}) as Record<string, unknown>}
              onChange={(config) =>
                setDraft((current) => ({ ...current, config }))
              }
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button onClick={() => onSave(draft)} disabled={isPending}>
            {isPending ? 'Saving...' : 'Save side effect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function buildEffect(handler: EffectHandlerCatalogEntry): WorkflowSideEffectEntry {
  return {
    handler: handler.key,
    config: {},
    phase: handler.default_phase ?? 'on_commit',
    failure_policy: handler.default_failure_policy ?? 'log_and_continue',
    idempotent: true,
    when: {},
  }
}

function buildEmptyEffect(): WorkflowSideEffectEntry {
  return {
    handler: '',
    config: {},
    phase: 'on_commit',
    failure_policy: 'log_and_continue',
    idempotent: true,
    when: {},
  }
}

function normaliseEffects(
  effects: WorkflowSideEffectEntry[] | undefined,
): WorkflowSideEffectEntry[] {
  return (effects ?? []).map((effect) => ({
    handler: effect.handler,
    config: effect.config ? structuredClone(effect.config) : {},
    phase: effect.phase ?? 'on_commit',
    failure_policy: effect.failure_policy ?? 'log_and_continue',
    idempotent: effect.idempotent ?? true,
    when: effect.when ? structuredClone(effect.when) : {},
  }))
}

function effectTitle(
  effect: WorkflowSideEffectEntry | undefined,
  byKey: Map<string, EffectHandlerCatalogEntry>,
): string {
  if (!effect) return 'side effect'
  return byKey.get(effect.handler)?.label || humanize(effect.handler)
}

function effectSummary(
  effect: WorkflowSideEffectEntry,
  handler: EffectHandlerCatalogEntry | undefined,
): string {
  const config = (effect.config ?? {}) as Record<string, unknown>
  if (effect.handler === 'call_service') {
    const service = stringValue(config.service)
    return service ? `Calls ${service}` : 'No service configured.'
  }
  if (effect.handler === 'send_email') {
    const template = stringValue(config.template)
    const to = stringValue(config.to)
    const subject = stringValue(config.subject)
    const parts = [
      template ? `Template: ${template}` : 'No template configured',
      to ? `To: ${to}` : null,
      subject ? `Subject: ${subject}` : null,
    ].filter(Boolean)
    return parts.join(' | ')
  }
  return handler?.description || configSummary(config)
}

function configSummary(config: Record<string, unknown>): string {
  const entries = Object.entries(config)
  if (entries.length === 0) return 'No configuration set.'
  return entries
    .slice(0, 3)
    .map(([key, value]) => `${humanize(key)}: ${stringValue(value) || 'set'}`)
    .join(' | ')
}

function stringValue(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) return value.map(stringValue).filter(Boolean).join(', ')
  if (value && typeof value === 'object') return 'configured'
  return ''
}

function humanize(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}
